import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link as ScrollLink } from "react-scroll";
import Header from "./components/Header/Header";
import Home from "./pages/Home/Home";
import Resources from "./pages/Resources/Resources";
import OurPartners from "./pages/OurPartners/OurPartners";
import Products from "./pages/Products/Products";
import ReferalLink from "./pages/ReferalLink/ReferalLink";
import ContactUs from "./pages/ContactUs/ContactUs";
import PrivatePolicy from "./components/Cookie/PrivacyPolicy";
import CompanyBio from "./pages/Bio/CompanyBio";
import CompanyTeam from "./pages/CompanyTeam/CompanyTeam";
import FAQ from "./pages/FAQ/FAQ";
import ServicesHome from "./components/services/ServicesHome";
import QrMenuPage from "./pages/QrMenuPage/QrMenuPage";
import QrMenuDemo from "./pages/QrMenuPage/QrMenuDemo";
import DeliveryBlueprint from "./pages/DeliveryBlueprint/DeliveryBlueprint";
import ServiceLandingPage from "./pages/ServiceLanding/ServiceLandingPage";

// Create a Layout component
const Layout = ({ children }) => {
  return (
    <div>
      {/* Add a link to scroll to the top */}
      <ScrollLink to="top" smooth="true" duration={500}>
        {/* Scroll to Top */}
      </ScrollLink>

      {/* Header component */}
      <Header />

      {/* Page content */}
      {children}
    </div>
  );
};

// Language wrapper logic
const LanguageQueryWrapper = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const lang = searchParams.get("lang");

    if (["en", "ru", "hy", "el"].includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    } else {
      // If lang is missing or invalid in URL, fallback to Armenian
      searchParams.set("lang", "hy");
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.pathname, i18n, navigate]);

  return children;
};

// Main RoutesConfig component
const RoutesConfig = () => {
  const location = useLocation();
  // Simulate fetching user authentication data
  useEffect(() => {}, []);

  return (
    <LanguageQueryWrapper>
      <Routes>
        <Route
          path="plans-pricing"
          element={<Navigate to={`/resources-and-tools${location.search}`} replace />}
        />
        <Route
          path="/"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
        <Route
          path="resources"
          element={
            <Layout>
              <Resources />
            </Layout>
          }
        />
        <Route
          path="resources-and-tools"
          element={
            <Layout>
              <OurPartners />
            </Layout>
          }
        />
        <Route
          path="get-in-touch"
          element={
            <Layout>
              <ContactUs />
            </Layout>
          }
        />
        <Route
          path="products"
          element={
            <Layout>
              <Products />
            </Layout>
          }
        />
        <Route
          path="referal"
          element={
            <Layout>
              <ReferalLink />
            </Layout>
          }
        />
        <Route
          path="privacy-policy"
          element={
            <Layout>
              <PrivatePolicy />
            </Layout>
          }
        />
        <Route
          path="bio"
          element={
            <Layout>
              <CompanyBio />
            </Layout>
          }
        />
        <Route
          path="company-team"
          element={
            <Layout>
              <CompanyTeam />
            </Layout>
          }
        />
        <Route
          path="faq"
          element={
            <Layout>
              <FAQ />
            </Layout>
          }
        />
        <Route
          path="services"
          element={
            <Layout>
              <ServicesHome />
            </Layout>
          }
        />
        <Route
          path="qr-menu"
          element={
            <Layout>
              <QrMenuPage />
            </Layout>
          }
        />
        <Route path="services/:serviceSlug" element={<Layout><ServiceLandingPage /></Layout>} />
        <Route
          path="qr-menu-demo"
          element={
            <Layout>
              <QrMenuDemo />
            </Layout>
          }
        />
        <Route
          path="delivery-blueprint"
          element={
            <Layout>
              <DeliveryBlueprint />
            </Layout>
          }
        />
        <Route
          path="packages"
          element={<Navigate to={`/services${location.search}`} replace />}
        />
        <Route
          path="*"
          element={
            <Layout>
              <section style={{ padding: '80px 24px', textAlign: 'center' }}>
                <h1>404</h1>
                <p>Page Not Found</p>
              </section>
            </Layout>
          }
        />
      </Routes>
    </LanguageQueryWrapper>
  );
};

export default RoutesConfig;
